import { Tag } from "@blueprintjs/core";
import { getIn } from "formik";
import {
  FGCustomInput,
  FGTextInput,
  IFGFormGroupProps,
  useFGContext,
} from "nsitools-react";
import React from "react";

import {
  DataTranslationDto,
  DataTranslationValueDto,
  LanguageDto,
} from "../../api";
import { useDisplayLanguages } from "../../hooks";

export interface IFGTranslationsProps
  extends Omit<IFGFormGroupProps, "children" | "name"> {
  translationsField?: string;
  readonlyCurrentLanguage?: boolean;
}

export const FGTranslations: React.FunctionComponent<IFGTranslationsProps> = ({
  translationsField = "dataTranslation",
  label,
  requiredMark,
  readonlyCurrentLanguage = false,
  ...fieldProps
}) => {
  const { languages, loadingLanguages, currentLanguage } =
    useDisplayLanguages();
  const { formik, loading } = useFGContext();

  const [initialized, setInitialized] = React.useState(false);

  React.useEffect(() => {
    const currentDataTranslation: DataTranslationDto = getIn(
      formik.values,
      `${translationsField}`
    );

    const currentTranslationsInDto =
      currentDataTranslation?.dataTranslationValues;
    if (
      initialized ||
      !currentTranslationsInDto ||
      languages.length === 0 ||
      loadingLanguages ||
      loading
    )
      return;

    const missingLanguages = languages?.filter(
      (c) =>
        !currentTranslationsInDto.flatMap((cn) => cn.languageId).includes(c.id)
    );

    missingLanguages?.forEach((ml) => {
      currentTranslationsInDto.push(
        new DataTranslationValueDto({ languageId: ml.id })
      );
    });

    formik.setFieldValue(translationsField, currentDataTranslation);
    setInitialized(true);
  }, [
    formik,
    formik.values,
    initialized,
    languages,
    loading,
    loadingLanguages,
    translationsField,
  ]);

  const getFieldName = React.useCallback(
    (languageId: number) => {
      if (!formik?.values) return "";
      if (Object.keys(formik.values).every((k) => k !== translationsField)) {
        throw new Error(
          "The field " +
            translationsField +
            " does not exists in formik.values, check the field name !!"
        );
      }
      const currentDataTranslation: DataTranslationDto = getIn(
        formik.values,
        `${translationsField}`
      );

      const currentTranslationsInDto =
        currentDataTranslation?.dataTranslationValues ?? [];

      const index = currentTranslationsInDto.findIndex(
        (d) => d.languageId === languageId
      );
      return `${translationsField}.dataTranslationValues.${index}.text`;
    },
    [formik.values, translationsField]
  );

  const sortByOrderIndex = React.useCallback(
    (a: LanguageDto, b: LanguageDto) => {
      return a.orderIndex - b.orderIndex;
    },
    []
  );

  const sortedLanguages = React.useMemo(
    () => [...languages].sort(sortByOrderIndex),
    [languages, sortByOrderIndex]
  );

  // RENDER PART
  if (loadingLanguages) {
    return null;
  }

  return (
    <FGCustomInput label={label} requiredMark={requiredMark}>
      {() => (
        <>
          {fieldProps.readonly && readonlyCurrentLanguage
            ? languages
                .filter((lang) => lang.code === currentLanguage.code)
                ?.map((lang) => (
                  <FGTextInput
                    noLabel
                    key={lang.id}
                    name={getFieldName(lang.id)}
                    rightElement={<Tag>{lang.code.toUpperCase()}</Tag>}
                    {...fieldProps}
                  />
                ))
            : sortedLanguages?.map((lang) => (
                <FGTextInput
                  noLabel
                  key={lang.id}
                  name={getFieldName(lang.id)}
                  rightElement={<Tag>{lang.code.toUpperCase()}</Tag>}
                  {...fieldProps}
                />
              ))}
        </>
      )}
    </FGCustomInput>
  );
};
